import DESTINATION_ENUM from 'web/destinations/DESTINATION_ENUM';
import FlyInput from 'shared/components/Form/FlyInput.vue';
import FlyPrivateKeyFileInput from 'shared/components/Form/FlyPrivateKeyFileInput.vue';
import SnowflakeAuthenticationRadioCard from 'web/pipelines/create/destination/SnowflakeAuthenticationRadioCard.vue';
import { markRaw } from 'vue';
import getRegionSelectField from 'web/common/getRegionSelectField';
import { trim } from 'lodash';
import FlySingleSelect from 'shared/components/Form/FlySingleSelect.vue';
import S3_FILE_FORMAT_ENUM from 'web/common/S3_FILE_FORMAT_ENUM';
import FILE_FORMAT_ENUM from 'web/common/FILE_FORMAT_ENUM';
import PipelineS3AccessSetup from 'web/pipelines/create/PipelineS3AccessSetup';
import { CREATE_DESTINATION_FORM } from 'web/form/formKeys';
import PipelineCreateSSHOptions from 'web/pipelines/create/PipelineCreateSSHOptions';
import PipelineCreateUseSSLSwitch from 'web/pipelines/create/PipelineCreateUseSSLSwitch';
import CLOUD_PROVIDER_ENUM from 'web/common/CLOUD_PROVIDER_ENUM';

const commonInputProps = {
  labelClass: 'fly-input--label--common-form',
  class: 'fly-input fly-input--column',
};

const nameFieldMaxLength = {
  inputtype: 'node_name',
};

const nodeConfigMaxLength = {
  inputtype: 'node_config',
};

const SNOWFLAKE_AUTH_METHODS = {
  PASSWORD: 'password',
  KEY_PAIR: 'key_pair',
};

export default {
  [DESTINATION_ENUM.REDSHIFT]: {
    fieldGroups: [
      {
        title: 'Destination Details',
        fields: [
          {
            component: markRaw(FlyInput),
            formKey: 'name',
            changeEventName: 'input',
            props: {
              ...commonInputProps,
              ...nameFieldMaxLength,
              label: 'Destination Name *',
              autoFocus: true,
              placeholder: 'e.g., production-analytics, test-analytics, etc.',
            },
          },
        ],
      },
      {
        title: 'Destination Credentials',
        fields: [
          [
            {
              component: markRaw(FlyInput),
              formKey: 'config.connection_config.host',
              changeEventName: 'input',
              props: {
                ...commonInputProps,
                label: 'Host *',
                autoFocus: true,
                placeholder:
                  'e.g., cluster-1.abcdefgh.us-east-1.redshift.amazonaws.com',
              },
              transformEventValue: (e) => trim(e.target.value),
            },
            {
              component: markRaw(FlyInput),
              formKey: 'config.connection_config.port',
              changeEventName: 'input',
              props: {
                ...commonInputProps,
                type: 'number',
                label: 'Port *',
              },
              defaultValue: 5439,
            },
          ],
          [
            {
              component: markRaw(FlyInput),
              formKey: 'config.connection_config.user',
              changeEventName: 'input',
              props: {
                ...commonInputProps,
                label: 'User *',
              },
              transformEventValue: (e) => trim(e.target.value),
            },
            {
              component: markRaw(FlyInput),
              formKey: 'config.connection_config.password',
              changeEventName: 'input',
              props: {
                ...commonInputProps,
                ...nodeConfigMaxLength,
                type: 'password',
                label: 'Password *',
              },
              ephemeral: true,
            },
          ],
          [
            {
              component: markRaw(FlyInput),
              formKey: 'config.connection_config.database',
              changeEventName: 'input',
              props: {
                ...commonInputProps,
                label: 'Database *',
              },
              transformEventValue: (e) => trim(e.target.value),
            },
            {
              component: markRaw(FlyInput),
              formKey: 'config.connection_config.pgschema',
              changeEventName: 'input',
              props: {
                ...commonInputProps,
                label: 'Schema *',
              },
              defaultValue: 'public',
              transformEventValue: (e) => trim(e.target.value),
            },
          ],
          {
            formKey: 'tunnel_id',
            defaultValue: null,
          },
          {
            formKey: 'config.destination_type',
            defaultValue: DESTINATION_ENUM.REDSHIFT,
          },
        ],
      },
      {
        title: 'Connection options',
        fields: [
          {
            component: markRaw(PipelineCreateSSHOptions),
          },
        ],
      },
      {
        title: 'Custom SSL options',
        fields: [
          {
            component: markRaw(PipelineCreateUseSSLSwitch),
            formKey: 'config.connection_config.ssl',
            defaultValue: {
              rejectUnauthorized: false,
            },
          },
        ],
      },
    ],
  },
  [DESTINATION_ENUM.SNOWFLAKE]: {
    fieldGroups: [
      {
        title: 'Destination Details',
        fields: [
          {
            component: markRaw(FlyInput),
            formKey: 'name',
            changeEventName: 'input',
            props: {
              ...commonInputProps,
              ...nameFieldMaxLength,
              label: 'Destination Name *',
              autoFocus: true,
              placeholder: 'e.g., production-analytics, test-analytics, etc.',
            },
          },
        ],
      },
      {
        title: 'Destination Credentials',
        fields: [
          {
            component: markRaw(FlyInput),
            formKey: 'config.connection_config.account',
            changeEventName: 'input',
            props: {
              ...commonInputProps,
              label: 'Account Locator + Region *',
              autoFocus: true,
              placeholder: 'e.g., xy12345.region.cloud',
              helpTextHtml:
                'Use the Account Locator format with region (e.g., xy12345.us-east-2.aws, xy12345.west-us-2.azure, xy12345.us-central1.gcp). ' +
                '<a href="https://www.integrate.io/docs/cdc/how-to-get-your-snowflake-account-identifier/" target="_blank" rel="noopener noreferrer">Find your Snowflake Account Locator and Region</a>.',
            },
            transformEventValue: (e) => {
              const trimmedValue = trim(e.target.value);
              return trimmedValue.replace('.snowflakecomputing.com', '');
            },
          },
          [
            {
              component: markRaw(FlyInput),
              formKey: 'config.connection_config.warehouse',
              changeEventName: 'input',
              props: {
                ...commonInputProps,
                label: 'Warehouse *',
                placeholder: '',
              },
              transformEventValue: (e) => trim(e.target.value),
            },
            {
              component: markRaw(FlyInput),
              formKey: 'config.connection_config.username',
              changeEventName: 'input',
              props: {
                ...commonInputProps,
                label: 'Username *',
              },
              transformEventValue: (e) => trim(e.target.value),
            },
          ],
          [
            {
              component: markRaw(SnowflakeAuthenticationRadioCard),
              formKey: 'config.connection_config.authentication_method',
              changeEventName: 'change',
              props: {
                ...commonInputProps,
              },
              relatedUpdates: [
                {
                  formKey: 'config.connection_config.password',
                  value: (changeEventValue, formData) => {
                    return changeEventValue === SNOWFLAKE_AUTH_METHODS.KEY_PAIR
                      ? null
                      : formData.config.connection_config.password;
                  },
                },
                {
                  formKey: 'config.connection_config.private_key',
                  value: (changeEventValue, formData) => {
                    return changeEventValue === SNOWFLAKE_AUTH_METHODS.PASSWORD
                      ? null
                      : formData.config.connection_config.private_key;
                  },
                },
                {
                  formKey: 'config.connection_config.passphrase',
                  value: (changeEventValue, formData) => {
                    return changeEventValue === SNOWFLAKE_AUTH_METHODS.PASSWORD
                      ? null
                      : formData.config.connection_config.passphrase;
                  },
                },
              ],
            },
          ],
          [
            {
              component: markRaw(FlyInput),
              formKey: 'config.connection_config.password',
              changeEventName: 'input',
              props: {
                ...commonInputProps,
                ...nodeConfigMaxLength,
                type: 'password',
                label: 'Password *',
              },
              ephemeral: true,
              showIf: (formData) =>
                formData.config?.connection_config?.authentication_method ===
                SNOWFLAKE_AUTH_METHODS.PASSWORD,
            },
            {
              component: markRaw(FlyPrivateKeyFileInput),
              formKey: 'config.connection_config.private_key',
              changeEventName: 'change',
              props: {
                ...nodeConfigMaxLength,
                labelClass: 'fly-doc--input-label',
                buttonText: 'Upload',
                accept: '.pem/.p8/.key/.pk8',
                label: 'Private Key File *',
                style: 'top: 0px;',
              },
              ephemeral: true,
              showIf: (formData) =>
                formData.config?.connection_config?.authentication_method ===
                SNOWFLAKE_AUTH_METHODS.KEY_PAIR,
            },
            {
              component: markRaw(FlyInput),
              formKey: 'config.connection_config.passphrase',
              changeEventName: 'change',
              props: {
                ...commonInputProps,
                ...nodeConfigMaxLength,
                type: 'password',
                label: 'Passphrase (For encrypted key pair)',
              },
              ephemeral: true,
              showIf: (formData) =>
                formData.config?.connection_config?.authentication_method ===
                SNOWFLAKE_AUTH_METHODS.KEY_PAIR,
            },
          ],
          {
            component: markRaw(FlyInput),
            formKey: 'config.connection_config.role',
            changeEventName: 'input',
            props: {
              ...commonInputProps,
              label: 'User Role *',
            },
            transformEventValue: (e) => trim(e.target.value),
          },
          {
            component: markRaw(FlyInput),
            formKey: 'config.connection_config.storage_integration_name',
            changeEventName: 'input',
            props: {
              ...commonInputProps,
              label: 'Storage Integration Name *',
            },
            transformEventValue: (e) => trim(e.target.value),
          },
          [
            {
              component: markRaw(FlyInput),
              formKey: 'config.connection_config.database',
              changeEventName: 'input',
              props: {
                ...commonInputProps,
                label: 'Database *',
              },
              transformEventValue: (e) => trim(e.target.value),
            },
            {
              component: markRaw(FlyInput),
              formKey: 'config.connection_config.snowflake_schema',
              changeEventName: 'input',
              props: {
                ...commonInputProps,
                label: 'Schema *',
              },
              defaultValue: 'PUBLIC',
              allowEditAfterCreate: false,
              transformEventValue: (e) => trim(e.target.value),
            },
          ],
          {
            formKey: 'config.destination_type',
            defaultValue: DESTINATION_ENUM.SNOWFLAKE,
          },
        ],
      },
    ],
  },
  [DESTINATION_ENUM.S3]: {
    fieldGroups: [
      {
        title: 'Destination Details',
        fields: [
          {
            component: markRaw(FlyInput),
            formKey: 'name',
            changeEventName: 'input',
            props: {
              ...commonInputProps,
              ...nameFieldMaxLength,
              label: 'Destination Name *',
              autoFocus: true,
              placeholder: 'e.g., production-analytics, test-analytics, etc.',
            },
          },
        ],
      },
      {
        title: 'Bucket Info & Access',
        fields: [
          {
            component: markRaw(FlyInput),
            formKey: 'config.connection_config.bucket_name',
            changeEventName: 'input',
            props: {
              ...commonInputProps,
              label: 'Bucket name *',
            },
            transformEventValue: (e) => trim(e.target.value),
          },
          {
            formKey: 'config.provider.type',
            defaultValue: CLOUD_PROVIDER_ENUM.AWS,
          },
          getRegionSelectField('Bucket region *', 'config.provider.region'),
          {
            component: markRaw(PipelineS3AccessSetup),
            formKey: 'config.connection_config.assume_role',
            changeEventName: 'change',
            props: {
              inputProps: {
                ...commonInputProps,
                label: 'Role ARN *',
              },
              formName: CREATE_DESTINATION_FORM,
            },
            allowEditAfterCreate: true,
            transformEventValue: (e) => trim(e.target.value),
          },
          {
            component: markRaw(FlyInput),
            formKey: 'config.connection_config.namespace',
            changeEventName: 'input',
            props: {
              ...commonInputProps,
              label: 'Prefix',
            },
            transformEventValue: (e) => trim(e.target.value) || null,
          },
          {
            component: markRaw(FlySingleSelect),
            formKey: 'config.file_format',
            changeEventName: 'change',
            props: {
              class: 'fly-single-select--common-form',
              label: 'File format',
              search: false,
              options: S3_FILE_FORMAT_ENUM,
            },
            allowEditAfterCreate: false,
            transformEventValue: (value) => value.value,
            transformValue: (value) => S3_FILE_FORMAT_ENUM[value],
            defaultValue: FILE_FORMAT_ENUM.AVRO,
          },
          {
            formKey: 'config.destination_type',
            defaultValue: DESTINATION_ENUM.S3,
          },
        ],
      },
    ],
  },
  [DESTINATION_ENUM.BIGQUERY]: {
    fieldGroups: [
      {
        title: 'Destination Details',
        fields: [
          {
            component: markRaw(FlyInput),
            formKey: 'name',
            changeEventName: 'input',
            props: {
              ...commonInputProps,
              label: 'Destination Name *',
              autoFocus: true,
              placeholder: 'e.g., production-analytics, test-analytics, etc.',
            },
          },
        ],
      },
      {
        title: 'Destination Credentials',
        fields: [
          [
            {
              component: markRaw(FlyInput),
              formKey: 'config.connection_config.project_id',
              changeEventName: 'input',
              props: {
                ...commonInputProps,
                label: 'Project ID *',
                autoFocus: true,
                placeholder: 'e.g., euphoric-effect-296205',
              },
              transformEventValue: (e) => trim(e.target.value),
            },
          ],
          [
            {
              component: markRaw(FlyInput),
              formKey: 'config.connection_config.credentials.client_email',
              changeEventName: 'input',
              props: {
                ...commonInputProps,
                label: 'Client Email *',
              },
              transformEventValue: (e) => trim(e.target.value),
            },
            {
              component: markRaw(FlyInput),
              formKey: 'config.connection_config.credentials.private_key',
              changeEventName: 'input',
              props: {
                ...commonInputProps,
                ...nodeConfigMaxLength,
                type: 'password',
                label: 'Private Key *',
              },
              ephemeral: true,
            },
          ],
          [
            {
              component: markRaw(FlyInput),
              formKey: 'config.dataset_id',
              changeEventName: 'input',
              props: {
                ...commonInputProps,
                label: 'Dataset ID *',
              },
              allowEditAfterCreate: false,
              transformEventValue: (e) => trim(e.target.value),
            },
          ],
          {
            formKey: 'config.destination_type',
            defaultValue: DESTINATION_ENUM.BIG_QUERY,
          },
        ],
      },
    ],
  },
  [DESTINATION_ENUM.TEST]: {
    fieldGroups: [
      {
        title: 'Destination Details',
        fields: [
          {
            component: markRaw(FlyInput),
            formKey: 'name',
            changeEventName: 'input',
            props: {
              ...commonInputProps,
              ...nameFieldMaxLength,
              label: 'Destination Name *',
              autoFocus: true,
              placeholder: 'e.g., debug-sink, local-test, etc.',
            },
          },
          {
            formKey: 'config.destination_type',
            defaultValue: DESTINATION_ENUM.TEST,
          },
        ],
      },
    ],
  },
};
